/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useRef, useState, useEffect, useImperativeHandle } from 'react';
import { Dialog, Box, DialogTitle, IconButton, Card, CardContent, MenuItem, useTheme, Select, Stack, TextField, useMediaQuery, Typography } from '@mui/material';
import ShowPlugin from './ShowPlugin.js'
import { IoClose } from 'react-icons/io5';
import { ScrollableDialogContent } from '../Components/ScrollableDialogContent.js';

const PluginList = React.forwardRef(({ default_data, showAddDataset, handleBotMessage, showPromptList, secrets, datasets, datasetPermissions, handlePluginClick, triggerPluginList, setTriggerPluginList, getToken, getUser }, ref) => {
  const [plugins, setPlugins] = useState([]);
  const [pluginCategories, setPluginCategories] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [filteredPlugins, setFilteredPlugins] = useState([]);
  const [categoryValue, setCategoryValue] = useState('All');
  const [cur_plugin, setCurPlugin] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [triggerShowPlugin, setTriggerShowPlugin] = useState(0);
  var loaded_static = false;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const pluginListRef = useRef(null);
  useImperativeHandle(ref, () => ({
    handlePromptClickExec,
    handlePluginClickExec
  }));

  const [modalIsOpen, setIsOpen] = React.useState(false);

  function closeModal() {
    setTriggerPluginList(0);
    setIsOpen(false);
  }

  function loadPlugins() {
    var token = getToken()
    if(token === false) {
      return;
    }

    var user = getUser()

    if(user === false || user == null) {
       window.location = '/login';
       return;
    }
  
    fetch(default_data.default_chat_service_url + '/get-plugins', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },        
      body: JSON.stringify({
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while getting data.');
      })
      .then(data => {
        if(data.status != 200) {
            return;
        }
        data = data.response;

        if(default_data.default_enable_text_to_speech === false) {
          data = data.filter((item) => item.plugin_name !== 'TEXT_TO_SPEECH')
        }

        setPlugins(data);
        setPluginCategories(data.map((item) => item.category).reduce((acc, cur) => acc.includes(cur) ? acc : [...acc, cur], []));
      })
      .catch(error => {
        console.log(error);
      });    
  }

  useEffect(() => {
    if (searchValue === '' && categoryValue === 'All') {
      setFilteredPlugins(plugins);
    } else {
      let filtered = plugins.filter((plugin) => {
        if (categoryValue !== 'All' && plugin.category !== categoryValue) {
          return false;
        }
        if (searchValue !== '' && !plugin.title.toLowerCase().includes(searchValue.toLowerCase())) {
          return false;
        }
        return true;
      });
      setFilteredPlugins(filtered);
    }
  }, [searchValue, categoryValue, plugins]);

  useEffect(() => {
    if (triggerPluginList === 1) {
      setErrorMessage('')
      setIsOpen(true)
    }
    else if (triggerPluginList === 0) {
      setIsOpen(false)
    }
  }, [triggerPluginList]);

  useEffect(() => {
    if(loaded || loaded_static) {
      return;
    }
    setLoaded(true)
    loaded_static = true;
    loadPlugins()
  }, [])
 

  const DisplayDataPublic = () => {
    return (filteredPlugins.map(
    (info)=>{ 
        return(
          <PluginVignette key={info.id} info={info} PluginClick={PluginClick} />
        )
    }));      
  }      

  const PluginVignette = ( {info, PluginClick }) => {

    return(
      <Card
        key={info.id}
        tag={info.id}
        className="plugin-item"
        onClick={PluginClick}
        variant='outlined'
        sx={{
          width: '100%',
          backgroundColor: theme.palette.card.backgroundColor,
          marginBottom: '1rem',
          border: theme.palette.card.border,
          cursor: 'pointer',
          '&:hover': theme.palette.card.hover,
        }}
      >
        <CardContent>
          <div tag={info.id} className="plugin-item-category">
            {info.category}
          </div>
          <div tag={info.id} className="plugin-item-title">
            {(info.title)}
          </div>
          {
            info.paid_only == true ? 
            <div tag={info.id} className="plugin-item-paid">
              Paid users only
            </div>
            :
            <div tag={info.id} className="plugin-item-paid">
              Free plugin
            </div>
          }
          <div tag={info.id} className="plugin-item-description">
            {(info.description)}
          </div>
        </CardContent>
      </Card>
    )
  }      

  const handlePromptClickExec = (field_name, value) => {
    if(pluginListRef.current !== null) {
      pluginListRef.current.handlePromptClickExec(field_name, value)
    }
  }


  const handlePluginClickExec = (field_name, value) => {
    if(pluginListRef.current !== null) {
      pluginListRef.current.handlePluginClickExec(field_name, value)
    }
  }

  function PluginClick(e) {    
    var tag = e.target.getAttribute('tag');
    if(tag == null) {
      return;
    }
    tag = tag.toString();
    plugins.forEach((element) => {
      if(element['id'] === tag) {
        if(element['fields'] != null) {
          setErrorMessage('')
          setCurPlugin(element)
          setTriggerShowPlugin(1)
        }
        else {
          setErrorMessage('This plugin is only available to Ask Sage paid members, become a member now for only $30/mo!')
        }
      }
    });    
  }

  const fullScreen = useMediaQuery(theme.breakpoints.down('mobile'));
  return (
    <>
      <Dialog
        ref={ref}
        open={modalIsOpen}
        onClose={closeModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth={true} 
        maxWidth="lg" 
        fullScreen={fullScreen}
      >
        <DialogTitle id="alert-dialog-title">
          <Box display={'flex'} justifyContent={'space-between'}>
            Plugins / Agents
            <IconButton onClick={closeModal}>
              <IoClose />
            </IconButton>
          </Box>
        </DialogTitle>
        <ScrollableDialogContent sx={{height: '80vh'}} modalIsOpen={modalIsOpen}>
          {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
          <Stack spacing={2} direction={isMobile ? 'column' : 'row'} sx={{padding: 2, paddingTop: 0}} width={'100%'}>
            <Stack direction="column" spacing={0}>
              <Typography variant="body2">Search</Typography>
              <TextField
                id="search"
                variant="outlined"
                size="small"
                fullWidth
                value={searchValue}
                sx={{
                  maxWidth: 400
                }}
                onChange={(e) => {
                  setSearchValue(e.target.value)
                }}
              />
            </Stack>
            <Stack direction="column" spacing={0}>
              <Typography variant="body2">Category</Typography>
              <Select
                labelId="category"
                variant="outlined"
                fullWidth
                size="small"
                value={categoryValue}
                onChange={(e) => {
                  setCategoryValue(e.target.value)
                }}
                sx={{minWidth: 120}}
              >
                <MenuItem value={'All'}>All</MenuItem>
                {pluginCategories.map((category) => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>
          <div className="plugin-container">              
            { 
              (plugins.length === 0) ?
                <div className="loading">Loading...</div> : 
                (filteredPlugins.length === 0) ?
                  <div className="loading">No result</div> :
                  <DisplayDataPublic />                  
            }
          </div>
        </ScrollableDialogContent>
      </Dialog>
      <ShowPlugin datasetPermissions={datasetPermissions} showAddDataset={showAddDataset} ref={pluginListRef} showPromptList={showPromptList} handleBotMessage={handleBotMessage} secrets={secrets} datasets={datasets} cur_plugin={cur_plugin} setTriggerPluginList={setTriggerPluginList} handlePluginClick={handlePluginClick} setTriggerShowPlugin={setTriggerShowPlugin} triggerShowPlugin={triggerShowPlugin} getToken={getToken} default_data={default_data} />     
    </>
  );
});

export default PluginList;